import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminCatalogProduct } from "../api/fetchCatalogProducts";
import { SaveCatalogProductRequest } from "../api/createCatalogProduct";
import {
  AdminCatalogCategory,
  fetchCatalogCategories,
} from "../api/fetchCatalogCategories";
import { getStages } from "@/modules/administration/features/StageManagement/api/getStages/getStagesApi";
import { Stage } from "@/modules/administration/features/StageManagement/api/getStages/types";

const catalogProductSchema = z.object({
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug can only contain lowercase letters, numbers, and single hyphens",
    ),
  name: z.string().min(1, "Name is required"),
  tagline: z.string().min(1, "Tagline is required"),
  badge: z.string().nullable(),
  categoryId: z.string().min(1, "Category is required"),
  audiences: z.array(z.string()),
  trades: z.array(z.string()),
  stageUuids: z.array(z.string()),
  thumbnailFileUuid: z.string().nullable(),
  featured: z.boolean(),
  featuredOrder: z.coerce.number().int().nullable(),
  landingPageKey: z.string().nullable(),
  sortOrder: z.coerce.number().int().min(0, "Sort order must be 0 or greater"),
  isPublished: z.boolean(),
});

export type CatalogProductFormData = z.infer<typeof catalogProductSchema>;

function getDefaultValues(
  product: AdminCatalogProduct | null | undefined,
): CatalogProductFormData {
  if (!product) {
    return {
      slug: "",
      name: "",
      tagline: "",
      badge: null,
      categoryId: "",
      audiences: [],
      trades: [],
      stageUuids: [],
      thumbnailFileUuid: null,
      featured: false,
      featuredOrder: null,
      landingPageKey: null,
      sortOrder: 0,
      isPublished: false,
    };
  }

  return {
    slug: product.slug,
    name: product.name,
    tagline: product.tagline,
    badge: product.badge,
    categoryId: product.category.id,
    audiences: product.audiences,
    trades: product.trades,
    stageUuids: product.stages.map((stage) => stage.id),
    thumbnailFileUuid: product.thumbnailFileUuid,
    featured: product.featured,
    featuredOrder: product.featuredOrder,
    landingPageKey: product.landingPageKey,
    sortOrder: product.sortOrder,
    isPublished: product.isPublished,
  };
}

interface UseCatalogProductFormParams {
  initialData?: AdminCatalogProduct | null;
  onSubmit: (data: SaveCatalogProductRequest) => Promise<void>;
}

export function useCatalogProductForm({
  initialData,
  onSubmit,
}: UseCatalogProductFormParams) {
  const isEditing = !!initialData;
  const [categories, setCategories] = useState<AdminCatalogCategory[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>("");
  const [thumbnailFileName, setThumbnailFileName] = useState<string>("");
  const [isThumbnailPickerOpen, setIsThumbnailPickerOpen] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [categoriesResponse, stagesResponse] = await Promise.all([
          fetchCatalogCategories({ pageSize: 1000, isActive: true }),
          getStages({ pageSize: 1000, status: "active" }),
        ]);
        setCategories(categoriesResponse.data || []);
        setStages(
          (stagesResponse.data || []).filter((stage) => !stage.isDeleted),
        );
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const form = useForm<CatalogProductFormData>({
    resolver: zodResolver(catalogProductSchema),
    defaultValues: getDefaultValues(initialData),
  });

  const { reset, handleSubmit, setValue } = form;

  useEffect(() => {
    reset(getDefaultValues(initialData));
    if (initialData?.thumbnailUrl) {
      setThumbnailDisplayUrl(initialData.thumbnailUrl);
      setThumbnailFileName("Catalog thumbnail");
    } else {
      setThumbnailDisplayUrl("");
      setThumbnailFileName("");
    }
  }, [initialData, reset]);

  const handleThumbnailSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const file = files[0];
      setThumbnailDisplayUrl(file.publicUrl);
      setThumbnailFileName(file.name);
      setValue("thumbnailFileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setIsThumbnailPickerOpen(false);
    },
    [setValue],
  );

  const handleRemoveThumbnail = useCallback(() => {
    setThumbnailDisplayUrl("");
    setThumbnailFileName("");
    setValue("thumbnailFileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  const onFormSubmit = async (data: CatalogProductFormData) => {
    await onSubmit({
      slug: data.slug,
      name: data.name,
      tagline: data.tagline,
      badge: data.badge || null,
      categoryId: data.categoryId,
      audiences: data.audiences,
      trades: data.trades,
      stageUuids: data.stageUuids,
      thumbnailFileUuid: data.thumbnailFileUuid,
      featured: data.featured,
      featuredOrder: data.featured ? data.featuredOrder : null,
      landingPageKey: data.landingPageKey || null,
      sortOrder: data.sortOrder,
      isPublished: data.isPublished,
    });
  };

  return {
    form,
    isEditing,
    categories,
    stages,
    loadingOptions,
    thumbnailDisplayUrl,
    thumbnailFileName,
    isThumbnailPickerOpen,
    openThumbnailPicker: useCallback(() => setIsThumbnailPickerOpen(true), []),
    closeThumbnailPicker: useCallback(
      () => setIsThumbnailPickerOpen(false),
      [],
    ),
    handleThumbnailSelected,
    handleRemoveThumbnail,
    submit: handleSubmit(onFormSubmit),
  };
}
